// Печатные листы инженерного блока: акт на оборудование, верификация судна, отчёт по заявке.
//
// ЧТО ЭТО И ЧЕМ НЕ ЯВЛЯЕТСЯ
// Это лист для человека: инженер везёт его на судно, показывает капитану, подшивает.
// Это НЕ документ системы. Ничего никуда не заливается: ни в Bitrix, ни на портал 73, ни
// в 1С. Номер акта присвоил портал, оригинал живёт там же, и на листе это написано словами.
// Заливкой PDF в Bitrix занимается портал (73-portal/src/worker.js:5204), и переносить её
// сюда не надо: у нас нет ни записи, ни права на неё.
//
// ПОЧЕМУ ОТДЕЛЬНОЕ ОКНО, А НЕ @media print НА СТРАНИЦЕ
// Тем же приёмом печатает сам портал (`actPrint` в 73-portal/public/app.js:4122): собирает
// самодостаточный HTML и открывает его отдельным документом. Причины две, и обе весомее
// вкуса. Первая: лист не наследует ни одного правила styles.css, поэтому «уплотнили
// интерфейс» никогда не сдвинет бумагу. Вторая — вот эта: сборка листа тут чистая функция
// «данные → строка», и её можно проверить целиком, без браузера и без отрисовки. Проверки
// в tests/print73.test.ts именно этим и заняты.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО ФАЙЛА: ЛИСТ НЕ ДОДУМЫВАЕТ
// Пустое место в зеркале печатается прочерком с расшифровкой внизу листа, а не остаётся
// пустым и не заполняется значением по умолчанию. Разница не косметическая — см. §«Отметки
// чек-листа» ниже: бланк портала печатает «Yes» там, где отметки не было вовсе.
//
// ЧЕГО ЗДЕСЬ НЕТ
// Фотографий. Байты снимков лежат в R2 портала 73, в ядре только ключи, и берутся они по
// одному запросу на снимок (api/p73.ts, `useФайлP73`). Тянуть их пачкой в момент печати
// значит ждать десятки мегабайт и получить пустые рамки там, где ключ R2 не заведён.
// Поэтому лист говорит, сколько снимков у записи и где они, а не делает вид, что их нет.

import type { АктКарточка, ВерификацияКарточка, ОтчётКарточка, СвежестьЗеркала73 } from '../api/p73'
import type { Бланк, СтрокаБланка } from './blanks73'
import { БЛАНКИ_СНЯТЫ, ФИРМА, бланкПоТипу } from './blanks73'
import { ЭТАПЫ_ОТЧЁТА, ОТМЕТКИ, ТИПЫ_ВЕРИФИКАЦИИ, названиеШаблона, поляЭтапа } from './p73'

// ─────────────────────────── мелочи ───────────────────────────

/** Экранирование. Кавычка тоже: значения уезжают и внутрь атрибутов. */
export function esc(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

const ПРОЧЕРК = '<span class="nil">—</span>'

/** Пусто → прочерк. Ноль и false — НЕ пусто: «0 %» на листе означает ноль процентов. */
export function зн(v: unknown): string {
  if (v === null || v === undefined) return ПРОЧЕРК
  if (typeof v === 'string' && !v.trim()) return ПРОЧЕРК
  if (Array.isArray(v)) {
    const части = v.filter((x) => x !== null && x !== undefined && String(x).trim() !== '')
    return части.length ? esc(части.join(' / ')) : ПРОЧЕРК
  }
  if (typeof v === 'boolean') return v ? 'Годное / Good' : 'Не годное / Not good'
  if (typeof v === 'object') return esc(JSON.stringify(v))
  return esc(v)
}

const дв = (n: number) => String(n).padStart(2, '0')

/** dd.mm.yyyy. Часовой пояс не трогаем: даты актов приходят днём, без времени. */
export function день(v?: string | null): string {
  if (!v) return ПРОЧЕРК
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : esc(v)
}

/** dd.mm.yyyy ЧЧ:ММ по часам того, кто печатает. */
export function моментом(d: Date): string {
  return `${дв(d.getDate())}.${дв(d.getMonth() + 1)}.${d.getFullYear()}`
    + ` ${дв(d.getHours())}:${дв(d.getMinutes())}`
}

/** Возраст словами. Отдельная функция, потому что «210 мин» человек не читает. */
export function возрастом(мин: number | null | undefined): string {
  if (мин === null || мин === undefined || !Number.isFinite(мин)) return 'неизвестно'
  const м = Math.max(0, Math.round(мин))
  if (м < 1) return 'меньше минуты назад'
  if (м < 60) return `${м} мин назад`
  const ч = Math.floor(м / 60)
  const ост = м % 60
  if (ч < 24) return ост ? `${ч} ч ${ост} мин назад` : `${ч} ч назад`
  const д = Math.floor(ч / 24)
  return `${д} сут ${ч % 24} ч назад`
}

/**
 * С какого возраста зеркала лист считается опасным. Порог НАШ, в источнике его нет:
 * синк инженерного блока ходит раз в несколько минут, поэтому два часа молчания — это
 * уже не «чуть отстал», а «что-то встало». Печатается на листе числом, чтобы человек
 * видел, чьё это суждение.
 */
export const ПОРОГ_ТРЕВОГИ_МИН = 120

// ─────────────────────────── общий каркас листа ───────────────────────────

export interface УсловияПечати {
  /** Ответ `/p73/equip-due?only=freshness`. null — спросить не удалось. */
  свежесть?: СвежестьЗеркала73 | null
  /** Момент печати. Параметром — чтобы проверки не зависели от часов машины. */
  сейчас?: Date
}

const СТИЛИ = `
@page { size: A4; margin: 16mm 14mm; }
body { font: 12.5px/1.4 'Times New Roman', serif; color: #000; max-width: 760px;
       margin: 16px auto; padding: 0 12px; }
h1 { font-size: 15px; text-align: center; margin: 0; }
h2 { font-size: 13px; text-align: center; font-weight: 400; margin: 2px 0; }
h3 { font-size: 12.5px; margin: 14px 0 4px; }
.num { text-align: center; font-weight: 700; margin: 8px 0; }
table { width: 100%; border-collapse: collapse; margin: 8px 0; }
td, th { border: 1px solid #000; padding: 4px 8px; vertical-align: top; text-align: left; }
th { background: #eef2f7; font-weight: 700; font-size: 11.5px; }
.k { width: 55%; }
.intro { font-size: 12px; margin: 8px 0; text-align: justify; }
.nil { color: #888; }
.sigs { display: flex; gap: 30px; margin-top: 26px; font-size: 12px; }
.sig { flex: 1; }
.line { border-top: 1px solid #000; margin-top: 34px; padding-top: 3px; font-size: 11px; color: #333; }
.next { font-size: 11px; margin-top: 14px; font-style: italic; }
.cir-head { display: flex; justify-content: space-between; align-items: flex-start;
            border-bottom: 2px solid #2c4a7c; padding-bottom: 7px; margin-bottom: 5px; }
.cir-name { font-size: 13.5px; font-weight: 700; color: #2c4a7c; line-height: 1.12;
            font-family: Arial, sans-serif; }
.cir-contacts { font-size: 10px; color: #333; margin-top: 4px; }
.cir-addr { font-size: 10px; color: #333; text-align: right; line-height: 1.4; }
.cir-addr i { color: #888; }
.cir-cert { font-size: 9px; color: #555; margin: 0 0 10px; text-align: justify; line-height: 1.3; }
.cir-cert i { color: #888; }
.cir-foot { margin-top: 22px; font-size: 10px; color: #999; }
.crm-mark { border: 1px solid #9aa4b2; background: #f4f6f9; padding: 6px 9px; margin: 0 0 12px;
            font-family: Arial, sans-serif; font-size: 9.5px; line-height: 1.45; color: #333;
            -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.crm-mark.alarm { border-color: #b42318; background: #fdf1f0; color: #7a1b13; }
.crm-mark b { color: inherit; }
.crm-mark ul { margin: 3px 0 0; padding-left: 16px; }
.blk { border: 1px solid #bbb; padding: 6px 9px; margin: 6px 0; }
.blk-title { font-weight: 700; margin-bottom: 4px; }
.absent { font-style: italic; color: #666; }
.noprint { padding: 8px 16px; font-weight: 700; margin-bottom: 10px; }
@media print {
  .noprint { display: none; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table, .blk { page-break-inside: avoid; }
  h3 { page-break-after: avoid; }
}
`

/** Шапка-бланк СиР: то же, что печатает портал (73-portal/public/app.js:4136). */
const ФИРМЕННАЯ_ШАПКА = `
<div class="cir-head">
  <div>
    <div class="cir-name">Связь и<br>Радионавигация</div>
    <div class="cir-contacts">${ФИРМА.телефон} &middot; ${ФИРМА.почта} &middot; ${ФИРМА.сайт}</div>
  </div>
  <div class="cir-addr">${ФИРМА.адрес}<br><i>${ФИРМА.адрес_en}</i></div>
</div>
<div class="cir-cert">${esc(ФИРМА.сертификаты)}<br><i>${esc(ФИРМА.сертификаты_en)}</i></div>`

/**
 * Служебная отметка CRM. Стоит ПЕРВОЙ на листе и подписана так, чтобы её нельзя было
 * принять за часть бланка: лист, который выглядит официально и несёт вчерашние цифры,
 * хуже отсутствия листа.
 */
export function отметкаCRM(
  чегоНет: string[],
  условия: УсловияПечати,
  чтоЭто: string,
): string {
  const сейчас = условия.сейчас ?? new Date()
  const св = условия.свежесть ?? null
  const старейшее = св ? св.oldest_min : null
  const тревога = старейшее !== null && старейшее !== undefined && старейшее > ПОРОГ_ТРЕВОГИ_МИН

  let строкаВозраста: string
  if (!св) {
    строкаВозраста = '<b>Возраст данных получить не удалось</b> — ядро не ответило на запрос'
      + ' о последней сверке с порталом. Считайте лист справкой, а не выпиской: насколько'
      + ' он отстал от портала, здесь неизвестно.'
  } else if (старейшее === null || старейшее === undefined) {
    строкаВозраста = '<b>Возраст данных неизвестен:</b> синхронизация с порталом 73 в ядре'
      + ' ни разу не отмечалась. Это не «данные свежие».'
  } else {
    const самая = [...св.freshness]
      .filter((r) => r.age_min !== null && r.age_min !== undefined)
      .sort((a, b) => (b.age_min ?? 0) - (a.age_min ?? 0))[0]
    строкаВозраста = `Зеркало сверялось с порталом 73 <b>${возрастом(старейшее)}</b>`
      + (самая ? ` (самое старое — ${esc(самая.label)}, ${esc(String(самая.last_sync_at ?? '')).slice(0, 16).replace('T', ' ')})` : '')
      + `. Наш порог тревоги — ${ПОРОГ_ТРЕВОГИ_МИН} мин.`
    if (тревога) строкаВозраста += ' <b>Порог превышен: сверьтесь с порталом 73 перед подписью.</b>'
    if (св.unknown.length) {
      строкаВозраста += ` По ${св.unknown.length} сущност${св.unknown.length === 1 ? 'и' : 'ям'}`
        + ' синк не отмечался ни разу: ' + esc(св.unknown.join(', ')) + '.'
    }
  }

  const список = чегоНет.length
    ? `<div>Чего на этом листе нет:<ul>${чегоНет.map((с) => `<li>${esc(с)}</li>`).join('')}</ul></div>`
    : ''

  return `<div class="crm-mark${тревога ? ' alarm' : ''}">
  <b>Служебная отметка CRM — не часть бланка.</b>
  ${esc(чтоЭто)} Лист собран в CRM ${моментом(сейчас)} по зеркалу портала 73.
  <b>Это не документ системы:</b> в Bitrix, в 1С и на портал 73 он не заливается,
  номер и оригинал остаются на портале.
  <div>${строкаВозраста}</div>
  ${список}
  <div>Прочерк «—» означает, что значения нет в зеркале, а не что графа пустая в оригинале.
  Формулировки бланка — копия шаблонов портала 73 от ${БЛАНКИ_СНЯТЫ}.</div>
</div>`
}

function страница(заголовок: string, тело: string): string {
  return `<!doctype html><html lang="ru"><head><meta charset="utf-8">`
    + `<title>${esc(заголовок)}</title><style>${СТИЛИ}</style></head><body>`
    + `<button class="noprint" onclick="window.print()">🖨 Печать / PDF</button>`
    + `${ФИРМЕННАЯ_ШАПКА}${тело}</body></html>`
}

// ─────────────────────────── акт на оборудование ───────────────────────────

/** Значение строки бланка для колонки прибора `u`. Массив = по значению на прибор. */
export function поПрибору(v: unknown, u: number): unknown {
  if (Array.isArray(v)) return v[u]
  return u > 0 ? undefined : v
}

function таблицаСтрок(строки: СтрокаБланка[], данные: Record<string, unknown>, единиц: number): string {
  const ячейки = (r: СтрокаБланка) => {
    if (r.per_unit && единиц > 1) {
      return Array.from({ length: единиц }, (_, u) => `<td>${зн(поПрибору(данные[r.k], u))}</td>`).join('')
    }
    const v = данные[r.k]
    const один = Array.isArray(v) ? v[0] : v
    return `<td${единиц > 1 ? ` colspan="${единиц}"` : ''}>${зн(один)}</td>`
  }
  return `<table>${строки.map((r) => (
    `<tr><td class="k">${esc(r.ru)}<br><i>${esc(r.en)}</i></td>${ячейки(r)}</tr>`
  )).join('')}</table>`
}

/** Все коды, которые бланк знает: по ним отделяются строки «копия не знает». */
function кодыБланка(б: Бланк): Set<string> {
  const s = new Set<string>()
  for (const r of б.rows ?? []) s.add(r.k)
  for (const сек of б.sections ?? []) for (const r of сек.rows) s.add(r.k)
  for (const r of б.extra ?? []) s.add(r.k)
  return s
}

export function листАкта(a: АктКарточка, условия: УсловияПечати = {}): string {
  const б = бланкПоТипу(a.type)
  const шапка = (a.form.header || {}) as Record<string, unknown>
  const строки = (a.form.rows || {}) as Record<string, unknown>
  const отметки = a.form.checks || {}
  const инженер = a.engineer?.name || a.engineer_name || ''

  // Сколько колонок под приборы. `units` в зеркале бывает пустым — тогда одна колонка:
  // додумывать «наверное, три» нельзя, лишняя пустая колонка читается как «прибора нет».
  const потолок = б?.units_max ?? 1
  const единиц = Math.min(Math.max(Number(a.units) || 1, 1), Math.max(потолок, 1))

  const чегоНет: string[] = []
  if (!б) {
    чегоНет.push(`Бланка «${a.type}» нет в копии шаблонов портала — значения показаны кодами`
      + ' полей. Так бывает, если на портале завели новый тип акта.')
  }
  if (a.has_scan) {
    чегоНет.push('Исходного скана акта (файл в хранилище портала). Он открывается в CRM в'
      + ' карточке акта, на бумагу отсюда не переносится.')
  }
  if (a.from_archive) {
    чегоНет.push('Часть граф: акт вытащен из архива задач Bitrix разбором документа, а не'
      + ' выпущен на портале — в данные попало только то, что удалось разобрать.')
  }
  if (!a.imo) {
    чегоНет.push('Номера ИМО: в акте он не заполнен, поэтому судно с карточкой CRM не связано.')
  }

  let тело = `<h1>${esc(б?.name || названиеШаблона(a.type))}</h1>`
  if (б?.name_en) тело += `<h2>${esc(б.name_en)}</h2>`
  тело += `<div class="num">№ ${a.number ? esc(a.number) : ПРОЧЕРК}</div>`

  // Шапка судна. Если графы нет в бланке акта, берём то же значение из строки зеркала —
  // это тот же акт, а не другой источник: `vessel_name/imo/mmsi` портал кладёт в колонки
  // при создании. Ничего кроме этих трёх не подставляем.
  const изСтроки: Record<string, unknown> = { vessel: a.vessel_name, imo: a.imo, mmsi: a.mmsi }
  const графы = б?.header ?? [
    { k: 'vessel', ru: 'Название судна', en: 'Name of ship' },
    { k: 'imo', ru: 'Номер ИМО', en: 'IMO number' },
    { k: 'mmsi', ru: 'Идентификационный номер (MMSI)', en: 'Identification number (MMSI)' },
  ]
  тело += `<table>${графы.map((h) => {
    const v = шапка[h.k] ?? изСтроки[h.k]
    return `<tr><td class="k">${esc(h.ru)}<br><i>${esc(h.en)}</i></td><td>${зн(v)}</td></tr>`
  }).join('')}</table>`

  if (б?.intro) тело += `<div class="intro">${esc(б.intro)}<br><i>${esc(б.intro_en || '')}</i></div>`
  if (б?.note) тело += `<div class="intro">${esc(б.note)}</div>`

  if (б?.rows) тело += таблицаСтрок(б.rows, строки, единиц)
  for (const сек of б?.sections ?? []) {
    тело += `<h3>${esc(сек.title)}</h3>` + таблицаСтрок(сек.rows, строки, единиц)
  }
  if (б?.extra) тело += таблицаСтрок(б.extra, строки, единиц)

  // Строки, которых копия бланка не знает. Молча выбросить их нельзя: это ровно тот
  // случай, когда портал ушёл вперёд, и человек должен увидеть значение, пусть и кодом.
  const известные = б ? кодыБланка(б) : new Set<string>()
  const лишние = Object.keys(строки).filter((k) => !известные.has(k))
  if (лишние.length) {
    тело += `<h3>Строки бланка, которых копия шаблона не знает</h3>`
      + `<table>${лишние.map((k) => (
        `<tr><td class="k">${esc(k)}</td><td>${зн(строки[k])}</td></tr>`
      )).join('')}</table>`
    чегоНет.push(`Подписей к ${лишние.length} строк${лишние.length === 1 ? 'е' : 'ам'} бланка:`
      + ' на портале форму изменили после того, как снята копия шаблонов. Они внизу листа, кодами.')
  }

  // Пункты формы 6.3.22.2 — только номер и значение: сами формулировки живут в бланке
  // Регистра, в зеркале их нет вовсе, и придумывать их за Регистр нельзя.
  const пункты = Object.keys(a.form.items || {})
  if (пункты.length) {
    пункты.sort((x, y) => x.localeCompare(y, undefined, { numeric: true }))
    тело += `<h3>Пункты формы 6.3.22.2</h3><table>${пункты.map((n) => (
      `<tr><td style="width:70px"><b>${esc(n)}</b></td><td>${зн((a.form.items || {})[n])}</td></tr>`
    )).join('')}</table>`
    чегоНет.push('Формулировок пунктов формы 6.3.22.2 — они есть только в бланке Регистра;'
      + ' на листе номер пункта и значение. Сами значения портал распознал из скана автоматически.')
  }

  // ОТМЕТКИ ЧЕК-ЛИСТА — здесь мы НАРОЧНО расходимся с порталом.
  // Бланк портала печатает `data.checks[i] === 'no' ? 'No' : 'Yes'`, то есть пункт, по
  // которому отметки не ставили вовсе, выходит на бумагу как «Yes» — как пройденная
  // проверка. У архивных актов отметок нет почти нигде. Мы печатаем прочерк.
  if (б?.checks?.length) {
    let без = 0
    тело += `<table><tr><th class="k">Пункт проверки / Check</th><th style="width:70px">Yes / No</th></tr>`
      + б.checks.map((c, i) => {
        const о = отметки[String(i)]
        const текст = о === 'no' ? 'No' : о === 'ok' ? 'Yes' : (о ? esc(о) : (без += 1, ПРОЧЕРК))
        return `<tr><td class="k">${esc(c)}</td><td>${текст}</td></tr>`
      }).join('') + `</table>`
    if (без) {
      чегоНет.push(`Отметок по ${без} пункт${без === 1 ? 'у' : 'ам'} чек-листа из ${б.checks.length}:`
        + ' в зеркале их нет, и лист ставит прочерк. Бланк портала в этом месте печатает «Yes»,'
        + ' то есть выдаёт непроставленную отметку за пройденную проверку.')
    }
  }

  if (a.form.note) тело += `<div class="intro"><b>Примечание:</b> ${esc(a.form.note)}</div>`
  if (a.form.parse_full_fail) {
    чегоНет.push('Части бланка: портал не смог разобрать исходный документ полностью'
      + ' (parse_full_fail). Сверяйтесь со сканом.')
  }
  if (б?.outro) тело += `<div class="intro">${esc(б.outro)}<br><i>${esc(б.outro_en || '')}</i></div>`

  тело += `<div class="sigs">
  <div class="sig">Представитель проверяющей организации<br><i>Testing company representative</i><br>
    ${esc(ФИРМА.полное)}<br><i>${esc(ФИРМА.полное_en)}</i>
    <div class="line">${инженер ? esc(инженер) + ' · подпись' : 'ФИО, подпись'}</div></div>
  <div class="sig">Представитель судовладельца<br><i>Shipowner representative</i>
    <div class="line">Название организации, ФИО, подпись</div></div>
</div>`
  if (!инженер) {
    чегоНет.push('ФИО того, кто выпустил акт: в зеркале оно не заполнено'
      + (a.from_archive ? ' (акт импортирован из архива задач Bitrix)' : '')
      + ' — в строке подписи оставлено место.')
  }

  тело += `<div style="margin-top:10px;font-size:12px">Дата / Date: ${день(a.act_date)}</div>`
  if (б?.footer_next) тело += `<div class="next">${esc(б.footer_next)}</div>`
  тело += `<div class="cir-foot">CIR/SPB-${esc((a.act_date || '').slice(0, 4) || String((условия.сейчас ?? new Date()).getFullYear()))}`
    + ` &middot; акт #${a.d1_id} в зеркале CRM</div>`

  const что = `Акт на оборудование ${a.number || `#${a.d1_id}`}, статус в зеркале — ${esc(a.status)}.`
  return страница(`Акт ${a.number || a.d1_id}`, отметкаCRM(чегоНет, условия, что) + тело)
}

// ─────────────────────────── верификация судна ───────────────────────────

function пары(строки: Array<[string, string]>): string {
  return `<table>${строки.map(([k, v]) => (
    `<tr><td class="k">${esc(k)}</td><td>${v}</td></tr>`
  )).join('')}</table>`
}

export function листВерификации(v: ВерификацияКарточка, условия: УсловияПечати = {}): string {
  const h = v.header || {}
  const судно = h.vessel_name || v.vessel?.name || ''
  const imo = h.imo || v.vessel?.imo || ''
  // mmsi_callsign в форме портала — «MMSI / позывной» одной строкой; MMSI это часть до «/».
  const mmsi = (h.mmsi_callsign ? String(h.mmsi_callsign).split('/')[0].trim() : '') || v.vessel?.mmsi || ''
  const инженер = v.engineer?.name || ''

  const чегоНет: string[] = []
  if (v.photos_total > 0) {
    чегоНет.push(`Фотографий (${v.photos_total} шт.): их байты остаются в хранилище портала 73,`
      + ' в ядре только ключи. Смотрите их в карточке верификации в CRM или на портале.')
  }
  // Флаг, порт и судовые контакты приезжают ТОЛЬКО из шапки, которую инженер правил в
  // форме (`__vessel_header`). В справочнике судов зеркала они есть, но ручка ядра их
  // не отдаёт — поэтому пустое поле здесь надо называть поимённо, а не одной строкой:
  // «флага нет» и «порта нет» — разные пробелы, и закрываются они по-разному.
  const изШапки: Array<[unknown, string]> = [
    [h.flag, 'Флага судна'],
    [h.port, 'Порта регистрации'],
    [h.ship_email, 'Судовой почты'],
    [h.ship_phone, 'Судового телефона'],
  ]
  for (const [значение, имя] of изШапки) {
    if (значение === null || значение === undefined || String(значение).trim() === '') {
      чегоНет.push(`${имя}: инженер не заполнил это поле в шапке формы, а из справочника`
        + ' судов ядро его в ответ не отдаёт.')
    }
  }
  чегоНет.push('Заказчика по сделке: поле есть в зеркале портала, но ручка ядра его не'
    + ' отдаёт — на листе только название и номер заявки.')
  if (v.meta.unknown_keys.length) {
    чегоНет.push(`Разделов формы, которых ядро не знает: ${v.meta.unknown_keys.join(', ')}.`
      + ' Форма портала ушла вперёд.')
  }
  if (!инженер) чегоНет.push('ФИО инженера: в зеркале оно не заполнено.')

  let тело = `<h1>${судно ? esc(судно) : 'Судно не указано'}</h1>`
    + `<h2>${esc(ТИПЫ_ВЕРИФИКАЦИИ[v.type] || v.type)} верификация`
    + `${v.meta.full_v3 ? ' (форма v3)' : ''}${v.meta.river_register ? ' · речной регистр' : ''}</h2>`

  тело += `<h3>Судно и заявка</h3>` + пары([
    ['Судно', зн(судно)],
    ['IMO / MMSI', `${зн(imo)} / ${зн(mmsi)}`],
    ['Флаг', зн(h.flag)],
    ['Порт регистрации', зн(h.port)],
    ['Судовая почта', зн(h.ship_email)],
    ['Судовой телефон', зн(h.ship_phone)],
    ['Заявка', v.deal ? `${зн(v.deal.title)}${v.deal.bitrix_id ? ` · #${v.deal.bitrix_id}` : ''}` : зн(null)],
    ['Место работ', зн(v.deal?.location)],
    ['Инженер', зн(инженер)],
    ['Заполнена', день(v.created_at)],
    ['Отправлена / рассмотрена', `${день(v.submitted_at)} / ${день(v.reviewed_at)}`],
    ['Статус в зеркале', зн(v.status)],
    ['Заполнено полей', v.fill_pct === null || v.fill_pct === undefined ? ПРОЧЕРК : `${v.fill_pct} %`],
    ['Найдено замечаний', v.defects_found === null || v.defects_found === undefined
      ? ПРОЧЕРК : String(v.defects_found)],
  ])
  if (!v.deal) {
    чегоНет.push('Заявки: верификация сделана по инициативе инженера, сделки у неё нет вовсе.')
  }

  for (const сек of v.sections) {
    тело += `<h3>${esc(сек.title)}</h3>`
    if (сек.absent) {
      тело += `<div class="absent">Оборудование отсутствует на судне — отмечено инженером.</div>`
      continue
    }
    if (!сек.fields.length) {
      тело += `<div class="absent">Раздел в данных пуст.</div>`
      continue
    }
    тело += пары(сек.fields.map((п) => [п.label || п.key, зн(п.value)]))
  }

  for (const блок of v.checklist) {
    тело += `<h3>Чек-лист: ${esc(блок.key)}</h3>`
    if (v.meta.absent_blocks?.[блок.key]) {
      тело += `<div class="absent">Оборудование отсутствует на судне — отмечено инженером.</div>`
      continue
    }
    блок.instances.forEach((экз, i) => {
      const нет = v.meta.absent_instances?.[блок.key]?.[String(i)]
      тело += `<div class="blk"><div class="blk-title">${esc(экз.label)}</div>`
      if (нет) {
        тело += `<div class="absent">Нет на судне.</div></div>`
        return
      }
      тело += `<table><tr><th class="k">Параметр проверки</th><th style="width:90px">Статус</th>`
        + `<th>Причина / комментарий</th></tr>`
        + экз.checks.map((п) => {
          const с = п.value === 'ok' ? 'норма'
            : п.value === 'not_ok' || п.value === 'no' ? 'ЗАМЕЧАНИЕ'
              : п.value ? esc(п.value) : ПРОЧЕРК
          return `<tr><td class="k">${esc(п.label)}</td><td>${с}</td><td>${п.note ? esc(п.note) : ''}</td></tr>`
        }).join('') + `</table></div>`
    })
  }

  const итог = v.meta.general_notes || v.notes
  if (итог) тело += `<h3>Общие замечания / итог</h3><div class="intro">${esc(итог)}</div>`

  тело += `<div class="sigs">
  <div class="sig">Инженер<br>${esc(ФИРМА.полное)}
    <div class="line">${инженер ? esc(инженер) + ' · подпись' : 'ФИО, подпись'}</div></div>
  <div class="sig">Капитан судна
    <div class="line">ФИО, подпись</div></div>
</div>`
  тело += `<div class="cir-foot">Верификация #${v.d1_id} в зеркале CRM</div>`

  const что = `Верификация судна #${v.d1_id}, статус в зеркале — ${esc(v.status)}.`
  return страница(`Верификация ${судно || v.d1_id}`, отметкаCRM(чегоНет, условия, что) + тело)
}

// ─────────────────────────── отчёт по заявке ───────────────────────────

/**
 * Значение поля отчёта. У полей-дат в схеме портала тип `date`, но в данных лежит
 * `YYYY-MM-DD` — приводим к dd.mm.yyyy, как это делает бланк портала (`fmtFullDate`).
 */
function значениеЭтапа(ключ: string, v: unknown): string {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && /date|deadline/.test(ключ)) {
    return день(v)
  }
  return зн(v)
}

export function листОтчёта(r: ОтчётКарточка, условия: УсловияПечати = {}): string {
  const судно = r.vessel?.name || ''
  const инженер = r.engineer?.name || ''

  const чегоНет: string[] = []
  if (r.photos_total > 0) {
    чегоНет.push(`Фотографий (${r.photos_total} шт.): их байты остаются в хранилище портала 73,`
      + ' в ядре только ключи. Смотрите их в карточке отчёта в CRM или на портале.')
  }
  чегоНет.push('Заказчика по заявке: поле есть в зеркале портала, но ручка ядра его не отдаёт.')
  чегоНет.push('Подписей полей нет в самих данных — на листе они из копии схемы формы'
    + ' портала 73. Поле, показанное кодом, значит, что форму на портале изменили.')
  if (r.meta.unknown_keys.length) {
    чегоНет.push(`Этапов, которых ядро не знает: ${r.meta.unknown_keys.join(', ')}.`)
  }
  if (!инженер) чегоНет.push('ФИО инженера: в зеркале оно не заполнено.')
  if (!судно) чегоНет.push('Судна: заявка в зеркале не связана с судном.')

  let тело = `<h1>${судно ? esc(судно) : 'Судно не указано'}</h1>`
    + `<h2>Отчёт по заявке${r.deal?.bitrix_id ? ` · #${r.deal.bitrix_id}` : ''}</h2>`

  тело += `<h3>Сводка</h3>` + пары([
    ['Судно', зн(судно)],
    ['IMO / MMSI', `${зн(r.vessel?.imo)} / ${зн(r.vessel?.mmsi)}`],
    ['Заявка', r.deal ? зн(r.deal.title) : зн(null)],
    ['Место работ', зн(r.deal?.location)],
    ['Инженер', зн(инженер)],
    ['Дата отчёта', день(r.created_at)],
    ['Отправлен / рассмотрен', `${день(r.submitted_at)} / ${день(r.reviewed_at)}`],
    ['Статус в зеркале', зн(r.status)],
    ['Заполнено полей', r.fill_pct === null || r.fill_pct === undefined ? ПРОЧЕРК : `${r.fill_pct} %`],
  ])

  // Порядок этапов — по схеме портала, а не по тому, как ключи легли в jsonb. Этап, по
  // которому в данных пусто, всё равно печатается: провал в середине формы читается как
  // «мы что-то потеряли», а «не заполнено» — как факт.
  const поКоду = new Map(r.stages.map((э) => [э.code, э]))
  // `repeat` берём из СХЕМЫ, а не из ответа: у этапа, которого в данных нет вовсе, ответа
  // нет тоже, а печатать его надо по-разному. Повторяющийся этап без записей — это
  // «не заполнено»; расписать его поля прочерками значило бы показать один пустой день
  // работ там, где не было ни одного.
  const повторяется = new Map(ЭТАПЫ_ОТЧЁТА.map((с) => [с.code, с.repeat]))
  const порядок = [
    ...ЭТАПЫ_ОТЧЁТА.map((с) => поКоду.get(с.code)
      ?? { code: с.code, title: с.title, repeat: с.repeat, photos: 0 }),
    ...r.stages.filter((э) => !ЭТАПЫ_ОТЧЁТА.some((с) => с.code === э.code)),
  ]

  for (const э of порядок) {
    тело += `<h3>${esc(э.title)}</h3>`
    if (повторяется.get(э.code) && !(э.blocks && э.blocks.length)) {
      тело += `<div class="absent">Не заполнено.</div>`
      continue
    }
    if (э.blocks && э.blocks.length) {
      for (const б of э.blocks) {
        тело += `<div class="blk"><div class="blk-title">${esc(б.label)}</div>`
          + пары(поляЭтапа(э.code, б.items).map((п) => [п.label, значениеЭтапа(п.key, п.value)]))
          + (б.photos ? `<div class="absent">Снимков в этом блоке: ${б.photos} — на лист не попали.</div>` : '')
          + `</div>`
      }
      continue
    }
    const поля = поляЭтапа(э.code, э.items)
    if (!поля.length) {
      тело += `<div class="absent">Не заполнено.</div>`
      continue
    }
    тело += пары(поля.map((п) => [п.label, значениеЭтапа(п.key, п.value)]))
  }

  тело += `<div class="sigs">
  <div class="sig">Инженер<br>${esc(ФИРМА.полное)}
    <div class="line">${инженер ? esc(инженер) + ' · подпись' : 'ФИО, подпись'}</div></div>
  <div class="sig">Капитан судна / Заказчик
    <div class="line">ФИО, подпись</div></div>
</div>`
  тело += `<div class="cir-foot">Отчёт #${r.d1_id} в зеркале CRM</div>`

  const что = `Отчёт по заявке #${r.d1_id}, статус в зеркале — ${esc(r.status)}.`
  return страница(`Отчёт ${судно || r.d1_id}`, отметкаCRM(чегоНет, условия, что) + тело)
}

// ─────────────────────────── открыть лист ───────────────────────────

export type ИсходПечати = 'ok' | 'blocked'

/**
 * Открыть готовый лист отдельным документом. Всплывающее окно может быть заблокировано —
 * тогда возвращаем 'blocked', и экран говорит это словами: молча ничего не сделать
 * в ответ на нажатие «Печать» хуже, чем отказать.
 */
export function открытьЛист(html: string, окно: Window | null = typeof window === 'undefined' ? null : window): ИсходПечати {
  const w = окно?.open('', '_blank')
  if (!w) return 'blocked'
  w.document.write(html)
  w.document.close()
  return 'ok'
}

/** Отметки чек-листа верификации — те же слова, что на экране (lib/p73.ts). */
export const словоОтметки = (v?: string | null) => (v ? ОТМЕТКИ[v] ?? v : '—')
